// Colorado State University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - CSU's Common Data Set FY2026 (CSU_CDS_FY2026_Access.pdf, March 2026),
//    linked from Institutional Research, Planning and Effectiveness's Common
//    Data Set page (the SOURCES url). CSU titles it by fiscal year; it is the
//    2025-2026 edition (C1 reports the Fall 2025 class). C1 applicants,
//    admits and enrollees (residency table: N/A); C8; C14; C21/C22.
//    Its cost section is for 2025-2026 and checks "not available at this
//    time", so costs come from the financial aid office instead.
//    Quirks: C8 marks the SAT or ACT "not considered for admission, even if
//    submitted", while the admissions page says scores are read if you ask;
//    the page follows the admissions page for the policy and shows the CDS
//    line beside it. C14 gives a July 31 closing date; the admissions page
//    says the last day to apply is July 15, which the page uses.
//  - CSU Admissions: Domestic Freshman Admissions (dates, materials, test
//    scores, decisions) and Competitive Majors (direct-entry requirements and
//    auditions). The dates are given for "the following year's semesters"
//    with the application opening August 1, so they are read as fall 2027.
//  - CSU Office of Financial Aid, Cost of Attendance 2026-2027 (undergraduate,
//    Colorado resident and nonresident, living on campus).
//  - College Scorecard, school id 126818 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'CSU Common Data Set 2025-2026 (FY2026)',
        url: 'https://www.ir.colostate.edu/common-data-set/',
        read: '2026-10-01',
    },
    freshmen: {
        label: 'CSU Admissions, Domestic Freshman Admissions',
        url: 'https://admissions.colostate.edu/apply/freshmen/',
        read: '2026-10-01',
    },
    competitive: {
        label: 'CSU Admissions, Competitive Majors',
        url: 'https://admissions.colostate.edu/apply/competitive-majors/',
        read: '2026-10-01',
    },
    costs: {
        label: 'CSU Office of Financial Aid, Cost of Attendance 2026-2027',
        url: 'https://financialaid.colostate.edu/cost-of-attendance/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?126818-Colorado-State-University-Fort-Collins',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'colorado-state-university',
    kbId: 'colorado_state_university',
    name: 'Colorado State University',
    shortName: 'Colorado State',
    location: 'Fort Collins, CO',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (CSU brand color page); decoration only.
    branding: {
        primary: '#1E4D2B',
        secondary: '#C8C372',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/colorado_state_university_logo.png',
    },

    // The direct answer and short FAQ (#4090, founder 2026-10-13: content
    // only, no title change before the Oct 27 title-test read). Every figure
    // restates one the page already shows from the source named beside it;
    // CollegePages.test.jsx fails a number the rest of the page does not hold.
    // The search it answers: colorado state acceptance rate and csu acceptance rate.
    faq: {
        answer: { text: 'Colorado State University (CSU) admitted 88.6% of first-year applicants for fall 2025, 34,004 of the 38,365 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1' },
        questions: [
            { q: 'What is the CSU acceptance rate?', a: 'For fall 2025 entry, CSU admitted 34,004 of 38,365 first-year applicants, an acceptance rate of 88.6%, and 5,376 enrolled. CSU reports one rate for the whole university, with no rate by major.', source: 'cds', where: 'section C1' },
            { q: 'Does Colorado State have early action?', a: 'Yes. For fall 2027 entry the early action date is November 15, and it is nonbinding. CSU releases fall decisions on a rolling basis from October 1, and the last day to apply is July 15.', source: 'freshmen' },
            { q: 'Do some CSU majors have a higher bar?', a: 'Yes. For direct entry, Biomedical Sciences and Engineering ask for a 3.3 high school GPA with math and science course rules, Computer Science a 3.3 (or a 3.0 with four years of calculus-track math), and the College of Business a 3.2. Dance, Music and Theatre need an audition. If you miss a bar, CSU can still admit you to your alternate major or Exploratory Studies.', source: 'competitive' },
            { q: 'Does Colorado State require the SAT or ACT?', a: 'No. CSU is test-optional. If you send scores, they are reviewed only if you email admissions to ask, and they play no part in scholarships.', source: 'freshmen' },
            { q: 'What does a year at Colorado State cost?', a: 'CSU estimates $37,126 for Colorado residents and $61,026 for nonresidents living on campus in 2026-2027, with tuition, fees, housing, food, books, transportation and personal costs.', source: 'costs' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 38365,
        admitted: 34004,
        enrolled: 5376,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'CSU\'s Common Data Set reports one first-year acceptance rate for the whole university. CSU publishes the extra requirements for its competitive majors, but no acceptance rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'CSU has no early decision plan. Its early action date (November 15) is nonbinding and unrestricted, and the Common Data Set gives no early action counts.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to CSU and name a major. A few majors set a higher bar for direct entry; if you miss it, CSU can still admit you to your alternate major or an Exploratory Studies track:',
        rows: [
            { school: 'Biomedical Sciences', requirement: 'A 3.3 high school GPA; four years of calculus-track math; three years of science, at least two of them biology and chemistry, with grades of C or better; no F in any math or science course unless retaken for a C or better.' },
            { school: 'College of Business', requirement: 'A 3.2 high school GPA. The college offers one degree, the B.S. in Business Administration, with concentrations.' },
            { school: 'Computer Science', requirement: 'A 3.3 high school GPA, or a 3.0 to 3.29 GPA with four years of calculus-track math (AP Computer Science can stand in for the fourth year) and no D or F in math or AP Computer Science unless retaken for a C or better.' },
            { school: 'Engineering', requirement: 'A 3.3 high school GPA, four years of calculus-track math with grades of C or better and no D or F in math unless retaken, and a year of chemistry or physics.' },
            { school: 'Dance, Music and Theatre', requirement: 'An audition: Dance (B.A. and B.F.A.), Music (B.A. and B.M.) and Theatre (B.A.).' },
        ],
        source: 'competitive',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Early action date', value: 'November 15' },
        rows: [
            { plan: 'Early action (nonbinding)', deadline: 'November 15', decision: 'Rolling, from October 1', binding: false },
            { plan: 'Regular decision date', deadline: 'January 15', decision: 'Rolling', binding: false },
            { plan: 'Last day to apply', deadline: 'July 15', decision: 'Rolling', binding: false },
        ],
        note: 'The application opens August 1. A complete file by November 15 gets full consideration and automatic scholarship review by January 15; by January 15, scholarship review by February 28. After January 15, applications are read on a rolling basis and scholarships may not be available. CSU releases fall decisions from October 1, most within four weeks of a complete file (allow up to six). The fee is $65; Colorado students apply free.',
        source: 'freshmen',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'CSU does not require the SAT or ACT. If you send scores, they are added to your application but reviewed only if you email admissions to ask. Even then they carry little weight, are never the reason an application is declined, and play no part in scholarships. Scores can be used for composition placement after admission.',
        source: 'freshmen',
        cdsLine: 'The Common Data Set marks the SAT or ACT as not considered for admission, even if submitted; this page follows the admissions office, which reads scores when you ask.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshmen',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [14160, 37502] },
            { item: 'Housing (on campus)', amounts: [8584, 8584] },
            { item: 'Food', amounts: [9888, 9888] },
            { item: 'Books and supplies', amounts: [1200, 1200] },
            { item: 'Transportation', amounts: [1512, 2070] },
            { item: 'Personal expenses', amounts: [1782, 1782] },
        ],
        totals: [37126, 61026],
        note: 'Estimates for a full-time undergraduate living on campus, nine-month year. Freshmen pay no differential tuition.',
        source: 'costs',
        netPrice: {
            average: 22247,
            byIncome: [
                { income: '$0 to $30,000', amount: 12291 },
                { income: '$30,001 to $48,000', amount: 12331 },
                { income: '$48,001 to $75,000', amount: 16009 },
                { income: '$75,001 to $110,000', amount: 21777 },
                { income: 'Over $110,000', amount: 32378 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from CSU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
